import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Package, Mail, TrendingUp, Truck, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STATUS_LABEL = {
  booked: "Booked",
  dispatched: "Dispatched",
  in_transit: "In Transit",
  delivered: "Delivered",
  billed: "Billed",
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-black/10 p-6">
    <div className="flex items-center justify-between">
      <p className="font-mono-label text-[10px] text-black/50">{label}</p>
      <Icon size={18} className={accent ? "text-[#FF4500]" : "text-black/40"} strokeWidth={1.5}/>
    </div>
    <p className="font-display font-bold text-4xl mt-3">{value ?? "—"}</p>
  </div>
);

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("requests");
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loads, setLoads] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ carrier_name: "", carrier_email: "", origin: "", destination: "", truck_type: "Dry Van", rate: "", broker: "", pickup_date: "", delivery_date: "", status: "booked" });

  const refresh = async () => {
    try {
      const [s, r, l] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/dispatch-requests"),
        api.get("/admin/loads"),
      ]);
      setStats(s.data); setRequests(r.data); setLoads(l.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load admin data");
    }
  };

  useEffect(() => { refresh(); }, []);

  const updateRequestStatus = async (id, status) => {
    try {
      await api.patch(`/admin/dispatch-requests/${id}`, { status });
      toast.success("Request updated");
      refresh();
    } catch { toast.error("Update failed"); }
  };

  const updateLoadStatus = async (id, status) => {
    try {
      await api.patch(`/admin/loads/${id}/status`, { status, note: `Status updated to ${STATUS_LABEL[status]}` });
      toast.success("Load updated");
      refresh();
    } catch { toast.error("Update failed"); }
  };

  const createLoad = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/loads", form);
      toast.success(`Created ${data.tracking_id}`);
      setShowCreate(false);
      setForm({ carrier_name: "", carrier_email: "", origin: "", destination: "", truck_type: "Dry Van", rate: "", broker: "", pickup_date: "", delivery_date: "", status: "booked" });
      refresh();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed to create load"); }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const baseInput = "w-full bg-white border border-black/15 px-3 py-2 text-sm focus:outline-none focus:border-[#FF4500]";

  return (
    <div className="bg-[#F8F9FA] min-h-screen" data-testid="admin-page">
      <header className="bg-white border-b border-black/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono-label text-black/60 hover:text-[#FF4500]" data-testid="admin-back-link">
            <ArrowLeft size={14}/> BACK TO SITE
          </Link>
          <div className="text-right">
            <p className="font-mono-label text-[10px] text-[#FF4500]">DISPATCH CONSOLE</p>
            <p className="font-display font-semibold text-lg">Adtrax Admin</p>
          </div>
          <button onClick={doLogout} className="hidden md:inline-flex items-center gap-2 text-sm font-mono-label text-black/60 hover:text-[#FF4500]" data-testid="admin-logout">
            <LogOut size={14}/> LOGOUT
          </button>
        </div>
        {user && (
          <div className="max-w-7xl mx-auto px-6 md:px-10 pb-3 text-xs text-black/50 font-mono-label">
            SIGNED IN AS <span className="text-black">{user.email}</span>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard icon={Mail} label="SIGNUPS (NEW)" value={stats?.requests_new} accent />
          <StatCard icon={Mail} label="SIGNUPS (TOTAL)" value={stats?.requests_total} />
          <StatCard icon={Truck} label="LOADS" value={stats?.loads_total} />
          <StatCard icon={TrendingUp} label="ACTIVE" value={stats?.loads_active} accent />
          <StatCard icon={Package} label="DELIVERED" value={stats?.loads_delivered} />
        </div>

        <div className="mt-10 flex items-center gap-2 border-b border-black/10">
          {["requests", "loads"].map((t) => (
            <button
              key={t} onClick={() => setTab(t)}
              className={`px-5 py-3 font-mono-label text-xs ${tab === t ? "border-b-2 border-[#FF4500] text-black" : "text-black/50"}`}
              data-testid={`tab-${t}`}
            >
              {t === "requests" ? "CARRIER SIGNUPS" : "LOADS"}
            </button>
          ))}
          <div className="ml-auto">
            {tab === "loads" && (
              <button onClick={() => setShowCreate(true)} className="sharp-btn bg-black text-white px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-[#FF4500]" data-testid="admin-create-load">
                <Plus size={14}/> New Load
              </button>
            )}
          </div>
        </div>

        {tab === "requests" && (
          <div className="mt-6 bg-white border border-black/10 overflow-x-auto" data-testid="requests-table">
            <table className="w-full text-sm">
              <thead className="border-b border-black/10 text-left">
                <tr className="text-[10px] font-mono-label text-black/50">
                  <th className="p-4">RECEIVED</th>
                  <th className="p-4">CARRIER</th>
                  <th className="p-4">EQUIPMENT</th>
                  <th className="p-4">HOME BASE</th>
                  <th className="p-4">FLEET</th>
                  <th className="p-4">STATUS</th>
                  <th className="p-4">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-black/40">No signups yet.</td></tr>}
                {requests.map((q) => (
                  <tr key={q.id} className="border-b border-black/5">
                    <td className="p-4 font-mono-label text-[10px] text-black/60">{new Date(q.created_at).toLocaleDateString()}</td>
                    <td className="p-4">
                      <p className="font-medium">{q.name}</p>
                      <p className="text-xs text-black/50">{q.email} · {q.phone}</p>
                      {q.mc_number && <p className="text-xs text-black/50">MC {q.mc_number}</p>}
                    </td>
                    <td className="p-4">{q.truck_type}</td>
                    <td className="p-4">{q.home_base || "—"}</td>
                    <td className="p-4">{q.fleet_size || "—"}</td>
                    <td className="p-4">
                      <span className="px-2 py-1 text-[10px] font-mono-label bg-[#F1F3F5]">{q.status?.toUpperCase()}</span>
                    </td>
                    <td className="p-4">
                      <select value={q.status} onChange={(e) => updateRequestStatus(q.id, e.target.value)} className="text-xs border border-black/15 px-2 py-1" data-testid={`request-status-${q.id}`}>
                        <option value="new">new</option>
                        <option value="contacted">contacted</option>
                        <option value="onboarding">onboarding</option>
                        <option value="dispatching">dispatching</option>
                        <option value="lost">lost</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "loads" && (
          <div className="mt-6 bg-white border border-black/10 overflow-x-auto" data-testid="loads-table">
            <table className="w-full text-sm">
              <thead className="border-b border-black/10 text-left">
                <tr className="text-[10px] font-mono-label text-black/50">
                  <th className="p-4">LOAD ID</th>
                  <th className="p-4">CARRIER</th>
                  <th className="p-4">LANE</th>
                  <th className="p-4">EQUIP</th>
                  <th className="p-4">RATE</th>
                  <th className="p-4">BROKER</th>
                  <th className="p-4">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {loads.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-black/40">No loads yet.</td></tr>}
                {loads.map((s) => (
                  <tr key={s.id} className="border-b border-black/5">
                    <td className="p-4 font-mono-label text-xs text-[#FF4500]">{s.tracking_id}</td>
                    <td className="p-4">{s.carrier_name}</td>
                    <td className="p-4">{s.origin} → {s.destination}</td>
                    <td className="p-4">{s.truck_type}</td>
                    <td className="p-4">{s.rate || "—"}</td>
                    <td className="p-4">{s.broker || "—"}</td>
                    <td className="p-4">
                      <select value={s.status} onChange={(e) => updateLoadStatus(s.id, e.target.value)} className="text-xs border border-black/15 px-2 py-1" data-testid={`load-status-${s.id}`}>
                        {Object.keys(STATUS_LABEL).map((k) => <option key={k} value={k}>{k}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={createLoad} className="bg-white border border-black w-full max-w-2xl p-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[90vh] overflow-y-auto" data-testid="new-load-modal">
            <div className="md:col-span-2">
              <p className="font-mono-label text-xs text-[#FF4500]">NEW LOAD</p>
              <h3 className="font-display font-bold text-2xl mt-2">Book a load</h3>
            </div>
            <div>
              <label className="font-mono-label text-[10px] text-black/50">CARRIER NAME</label>
              <input required value={form.carrier_name} onChange={(e) => setForm({...form, carrier_name: e.target.value})} className={baseInput} data-testid="load-input-carrier"/>
            </div>
            <div>
              <label className="font-mono-label text-[10px] text-black/50">CARRIER EMAIL</label>
              <input value={form.carrier_email} onChange={(e) => setForm({...form, carrier_email: e.target.value})} className={baseInput}/>
            </div>
            <div>
              <label className="font-mono-label text-[10px] text-black/50">ORIGIN</label>
              <input required value={form.origin} onChange={(e) => setForm({...form, origin: e.target.value})} className={baseInput} data-testid="load-input-origin"/>
            </div>
            <div>
              <label className="font-mono-label text-[10px] text-black/50">DESTINATION</label>
              <input required value={form.destination} onChange={(e) => setForm({...form, destination: e.target.value})} className={baseInput} data-testid="load-input-destination"/>
            </div>
            <div>
              <label className="font-mono-label text-[10px] text-black/50">TRUCK TYPE</label>
              <select value={form.truck_type} onChange={(e) => setForm({...form, truck_type: e.target.value})} className={baseInput}>
                {["Dry Van","Reefer","Flatbed","Step Deck","Power Only","Hotshot"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono-label text-[10px] text-black/50">RATE</label>
              <input value={form.rate} onChange={(e) => setForm({...form, rate: e.target.value})} placeholder="$2,850" className={baseInput}/>
            </div>
            <div>
              <label className="font-mono-label text-[10px] text-black/50">BROKER</label>
              <input value={form.broker} onChange={(e) => setForm({...form, broker: e.target.value})} className={baseInput}/>
            </div>
            <div />
            <div>
              <label className="font-mono-label text-[10px] text-black/50">PICKUP DATE</label>
              <input type="date" value={form.pickup_date} onChange={(e) => setForm({...form, pickup_date: e.target.value})} className={baseInput}/>
            </div>
            <div>
              <label className="font-mono-label text-[10px] text-black/50">DELIVERY DATE</label>
              <input type="date" value={form.delivery_date} onChange={(e) => setForm({...form, delivery_date: e.target.value})} className={baseInput}/>
            </div>
            <div className="md:col-span-2 flex gap-3 justify-end mt-2">
              <button type="button" onClick={() => setShowCreate(false)} className="sharp-btn border border-black px-4 py-2 text-sm hover:bg-black hover:text-white">Cancel</button>
              <button type="submit" className="sharp-btn bg-[#FF4500] text-white px-4 py-2 text-sm hover:bg-black" data-testid="load-submit">Create Load</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
